import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Plus, Search, Mail, Phone, ArrowUpDown, Edit2, Save, X, Linkedin,
  FileText, Upload, Download, Trash2, Calendar, MessageSquare,
  FolderInput, CheckSquare2, UserCheck, Building2, LayoutList, Tag, ChevronDown, Sparkles,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { contactsAPI, activitiesAPI, filesAPI, accountsAPI } from '@/lib/api';
import { listTeam, getCurrentUser } from '@/lib/auth';
import { useContactTypes } from '@/lib/useContactTypes';
import { useT } from '@/lib/LanguageContext';
import { useProject } from '@/lib/ProjectContext';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { safeFormat } from '@/lib/utils';
import TwinePanel from '@/components/twine/TwinePanel';
import NotesPanel from '@/components/notes/NotesPanel';
import { twineAPI, twineChanged } from '@/lib/twineAPI';

// Zod schema for contact creation
const contactSchema = z.object({
  full_name: z.string().optional().or(z.literal('')),
  email: z.string().optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  company: z.string().optional().or(z.literal('')),
  position: z.string().optional().or(z.literal('')),
  contact_type: z.string().min(1, 'Contact type is required'),
  kind: z.string().optional().or(z.literal('')),
  source: z.string().optional().or(z.literal('')),
  assigned_to: z.string().optional().or(z.literal('')),
  linkedin_url: z.string().optional().or(z.literal('')),
  street: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  state: z.string().optional().or(z.literal('')),
  postal_code: z.string().optional().or(z.literal('')),
  country: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  stage: z.string().optional().or(z.literal('')),
  language: z.string().optional().or(z.literal('')),
}).superRefine((data, ctx) => {
  // A "Company" listing (general info, no named person) needs the company name instead of a person's name.
  if (data.kind === 'company') {
    if (!(data.company || '').trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Company name is required', path: ['company'] });
  } else if (!(data.full_name || '').trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Full name is required', path: ['full_name'] });
  }
  const email = (data.email || '').trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'That email doesn\'t look right', path: ['email'] });
  } else if (data.kind !== 'company' && !email && !(data.phone || '').trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Add an email or a phone number', path: ['email'] });
  }
});

// "Not in the pipeline": suppliers, partners, people you just want on file. Stored as an empty stage.
const NO_STAGE = '__none';
const NO_STAGE_OPTION = { id: NO_STAGE, name: 'Not in the pipeline', color: 'bg-white text-[#94A3B8] border-dashed border-[#C9C0B0]' };
const fromStageValue = (v) => (v === NO_STAGE ? '' : v);

// Stage and type configurations matching Pipeline
const STAGES = [
  { id: 'new_lead', name: 'New Lead', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { id: 'contacted', name: 'Contacted', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { id: 'engaged', name: 'Engaged', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { id: 'demo_scheduled', name: 'Demo Scheduled', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { id: 'proposal_sent', name: 'Proposal Sent', color: 'bg-pink-100 text-pink-700 border-pink-300' },
  { id: 'won', name: 'Won', color: 'bg-green-100 text-green-700 border-green-300' },
  { id: 'lost', name: 'Lost', color: 'bg-gray-100 text-gray-600 border-gray-300' },
];

// CONTACT_TYPES is now loaded dynamically via useContactTypes() hook (company-specific)

// Backend accepts a fixed enum: apollo | manual | referral | website.
// Keep user-friendly labels but submit the backend value.
// Light, readable pill colours per source
const SOURCE_STYLES = {
  website:  'bg-sky-50 text-sky-800 border-sky-200',
  referral: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  manual:   'bg-[#F3EFE7] text-[#1E2A33] border-[#E3DDD1]',
  apollo:   'bg-violet-50 text-violet-800 border-violet-200',
  existing: 'bg-amber-50 text-amber-800 border-amber-200',
};

const SOURCES = [
  { id: 'website', label: 'Website' },
  { id: 'referral', label: 'Referral' },
  { id: 'manual', label: 'Manual / Cold Outreach' },
  { id: 'apollo', label: 'Imported' },
  { id: 'existing', label: 'Existing Customer' },
];

// Whether a contact record is an actual named person, or just a company's
// general info (no named contact found yet — e.g. from a bulk company import).
const KINDS = [
  { id: 'person', label: 'Person' },
  { id: 'company', label: 'Company' },
];

// Tolerant mapping for legacy labels coming from CSV imports etc.
const normalizeSource = (value) => {
  if (!value) return 'manual';
  const v = String(value).trim().toLowerCase();
  if (['apollo', 'manual', 'referral', 'website', 'existing'].includes(v)) return v;
  if (v === 'existing customer') return 'existing';
  if (v === 'linkedin' || v === 'cold outreach' || v === 'event' || v === 'other') return 'manual';
  return 'manual';
};

const ACTIVITY_TYPES = [
  { id: 'call', label: 'Call', icon: Phone },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { id: 'note', label: 'Note', icon: FileText },
  { id: 'meeting', label: 'Meeting', icon: Calendar },
];

// Deterministic color for stages the project configured but that aren't in
// the canonical STAGES list (e.g. "inquiry", "active_member" on Passport Plus).
const STAGE_FALLBACK_COLORS = [
  'bg-slate-100 text-slate-700 border-slate-300',
  'bg-blue-100 text-blue-700 border-blue-300',
  'bg-purple-100 text-purple-700 border-purple-300',
  'bg-amber-100 text-amber-700 border-amber-300',
  'bg-pink-100 text-pink-700 border-pink-300',
  'bg-green-100 text-green-700 border-green-300',
  'bg-cyan-100 text-cyan-700 border-cyan-300',
  'bg-gray-100 text-gray-600 border-gray-300',
];

const toTitleCase = (s) =>
  String(s || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Build the effective stage list for the active project. Projects may store
 * stages as dicts ({id, name, color, order}) or fall back to the canonical
 * STAGES. Always returns a non-empty list with stable shape.
 */
// A contact can be in several projects. Older records only have project_id.
const contactProjectIds = (c) => (c?.project_ids?.length ? c.project_ids : (c?.project_id ? [c.project_id] : []));

// Tap to add or take a contact off a project
const ProjectChips = ({ projects, value, onToggle, disabled }) => (
  <div className="flex flex-wrap gap-1.5 mt-1">
    {projects.map((p) => {
      const on = value.includes(p._id);
      return (
        <button key={p._id} type="button" disabled={disabled} onClick={() => onToggle(p._id, !on)}
          className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${on ? 'border-[#1E2A33] bg-[#1E2A33] text-white' : 'border-[#E3DDD1] text-[#5C6670] hover:border-[#1E2A33]'}`}>
          <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: p.color || '#A62B1F' }} />
          {p.name}{on && <span aria-hidden>✓</span>}
        </button>
      );
    })}
  </div>
);

const resolveStages = (project) => {
  const raw = project && Array.isArray(project.pipeline_stages) ? project.pipeline_stages : null;
  if (!raw || raw.length === 0) return STAGES;

  return raw.map((s, idx) => {
    if (typeof s === 'string') {
      const match = STAGES.find((x) => x.id === s);
      return (
        match || {
          id: s,
          name: toTitleCase(s),
          color: STAGE_FALLBACK_COLORS[idx % STAGE_FALLBACK_COLORS.length],
        }
      );
    }
    if (s && typeof s === 'object' && typeof s.id === 'string') {
      const match = STAGES.find((x) => x.id === s.id);
      return {
        id: s.id,
        name: s.name || (match ? match.name : toTitleCase(s.id)),
        color: match ? match.color : STAGE_FALLBACK_COLORS[idx % STAGE_FALLBACK_COLORS.length],
      };
    }
    return null;
  }).filter(Boolean);
};

// Stages for a contact: the pipeline of the first of its projects that has one, else the standard stages.
const stagesFor = (projects, ids) => {
  const p = (projects || []).find((x) => (ids || []).includes(x._id) && Array.isArray(x.pipeline_stages) && x.pipeline_stages.length);
  return resolveStages(p || null);
};

// Minimal inline error boundary so a render-time crash in the contact detail
// sheet shows a readable message instead of white-screening the whole page.
class DetailErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ContactDetail] render crash:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-6">
          <p className="text-sm font-semibold text-red-600" style={{ fontFamily: 'Manrope' }}>
            Something went wrong displaying this contact.
          </p>
          <p className="mt-2 text-xs text-[#5C6670] break-words">
            {String(this.state.error?.message || this.state.error)}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => {
              this.setState({ error: null });
              this.props.onReset?.();
            }}
          >
            Dismiss
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function ContactList() {
  const { projects } = useProject();
  const t = useT();
  const { types: CONTACT_TYPES, colorFor, labelFor } = useContactTypes();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    stage: undefined,
    contact_type: undefined,
    assigned_to: undefined,
    source: undefined,
    kind: undefined,
  });
  const [selectedProjectIds, setSelectedProjectIds] = useState([]); // [] = all projects
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Contact detail sheet
  const [selectedContact, setSelectedContact] = useState(null);
  // Re-subscribe (lift an email opt-out) — owners/admins only, consent note required
  const [resubOpen, setResubOpen] = useState(false);
  const [resubNote, setResubNote] = useState('');
  const [resubSaving, setResubSaving] = useState(false);
  const canResubscribe = (() => {
    const u = getCurrentUser();
    return !!u && (u.is_super_admin || ['owner', 'admin'].includes(u.role));
  })();
  const handleResubscribe = async () => {
    if (resubNote.trim().length < 5) { toast.error('Describe how they opted back in'); return; }
    setResubSaving(true);
    try {
      await contactsAPI.resubscribe(selectedContact._id, resubNote.trim());
      const email = (selectedContact.email || '').toLowerCase();
      const clear = (c) => ((c.email || '').toLowerCase() === email
        ? { ...c, email_opt_out: false, email_opt_out_at: null, email_opt_out_source: null } : c);
      setSelectedContact((prev) => (prev ? clear(prev) : prev));
      setContacts((prev) => prev.map(clear));
      loadActivities(selectedContact._id);
      toast.success('Re-subscribed — campaign emails can be sent to this contact again');
      setResubOpen(false);
      setResubNote('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to re-subscribe');
    } finally {
      setResubSaving(false);
    }
  };
  const [showContactDetail, setShowContactDetail] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Activities
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(null);
  const [activityFormData, setActivityFormData] = useState({ subject: '', body: '' });
  
  // Files
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // New contact project picker
  const [newContactProjectIds, setNewContactProjectIds] = useState([]);

  // New contact sheet
  const location = useLocation();
  const [showNewContact, setShowNewContact] = useState(false);
  useEffect(() => {
    if (showNewContact) setNewContactProjectIds(selectedProjectIds.filter((id) => projects.some((p) => p._id === id)));
  }, [showNewContact]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open "New Contact" dialog when navigated from the global "New" button
  useEffect(() => {
    if (location.state?.openNew) {
      setShowNewContact(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  const NEW_CONTACT_DEFAULTS = { kind: 'person', stage: 'new_lead' };
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: NEW_CONTACT_DEFAULTS,
  });
  const newKind = watch('kind') || 'person';
  const newStage = watch('stage') || 'new_lead';

  // Dedup
  const [deduping, setDeduping] = useState(false);

  const handleDedup = async () => {
    if (!window.confirm('Remove duplicate contacts (same email)? This cannot be undone.')) return;
    setDeduping(true);
    try {
      const res = await contactsAPI.deduplicate();
      const { deleted } = res.data;
      toast.success(`Removed ${deleted} duplicate contact${deleted !== 1 ? 's' : ''}`);
      await loadContacts();
    } catch (err) {
      toast.error('Dedup failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeduping(false);
    }
  };

  // CSV import
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [csvFallbackProjectId, setCsvFallbackProjectId] = useState('');
  const [csvFallbackContactType, setCsvFallbackContactType] = useState('');

  const [csvFile, setCSVFile] = useState(null);
  const [csvPreview, setCSVPreview] = useState([]);
  const [importing, setImporting] = useState(false);

  // Current user (for Assigned To display)
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('heyday_user') || '{}'); } catch { return {}; }
  })();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const params = {
        search: debouncedSearch || undefined,
        stage: filters.stage,
        contact_type: filters.contact_type,
        assigned_to: filters.assigned_to,
      };
      const response = await contactsAPI.list(params);
      setContacts(response.data || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Load contacts when filters or search changes
  useEffect(() => {
    loadContacts();
  }, [debouncedSearch, filters.stage, filters.contact_type, filters.assigned_to]);

  // Load team members once for assigned-to filter and bulk assign
  useEffect(() => {
    listTeam().then(data => setTeamMembers(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  // Effective stages for the active project (canonical + any project-specific
  // ones like "inquiry" / "active_member"). Used for filter dropdown, stage
  // selector inside the detail sheet, and badge colour lookups.
  const projectStages = useMemo(() => {
    const all = [...STAGES];
    (projects || []).forEach((p) => resolveStages(p).forEach((st) => { if (!all.some((x) => x.id === st.id)) all.push(st); }));
    return all;
  }, [projects]);

  // Sorted + client-side filtered contacts memo
  const sortedContacts = useMemo(() => {
    let sorted = [...contacts];

    // Client-side project filter (supports multi-select)
    if (selectedProjectIds.length > 0) {
      sorted = sorted.filter(c => contactProjectIds(c).some(id => selectedProjectIds.includes(id)));
    }

    // Client-side source filter (not supported as a server param)
    if (filters.source) {
      sorted = sorted.filter(c => c.source === filters.source);
    }

    // Client-side type filter — Person vs Company (not supported as a server param)
    if (filters.kind) {
      sorted = sorted.filter(c => (c.kind || 'person') === filters.kind);
    }

    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        if (sortConfig.key === 'created_at') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [contacts, sortConfig, filters.source, filters.kind, selectedProjectIds]);

  // Load activities for selected contact
  const loadActivities = async (contactId) => {
    try {
      setLoadingActivities(true);
      const response = await activitiesAPI.getByContact(contactId);
      const data = response?.data;
      // Defensive: always store an array so `.map(...)` at render time
      // can't crash the sheet.
      setActivities(
        Array.isArray(data)
          ? data
          : Array.isArray(data?.activities)
            ? data.activities
            : []
      );
    } catch (error) {
      console.error('Error loading activities:', error);
      setActivities([]);
      toast.error('Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  // Load files for selected contact
  const loadFiles = async (contactId) => {
    try {
      setLoadingFiles(true);
      const response = await filesAPI.listByContact(contactId);
      // Backend shape: `{ files: [...] }` when SharePoint is configured,
      // `{ files: [], message: "..." }` in placeholder mode. Coerce to
      // an array so the render path (`files.map(...)`) can't ever crash
      // on a non-array payload.
      const data = response?.data;
      let next = [];
      if (Array.isArray(data)) next = data;
      else if (data && Array.isArray(data.files)) next = data.files;
      // records from the files API carry _id/filename; the list below reads id/name
      setFiles(next.map((f) => ({ ...f, id: f.id || f._id, name: f.name || f.filename })));
    } catch (error) {
      console.error('Error loading files:', error);
      setFiles([]);
      // Don't show error toast for files as SharePoint might not be configured
    } finally {
      setLoadingFiles(false);
    }
  };

  // Handle row click — skip if inline edit is active or user clicked a checkbox
  const handleRowClick = (contact, e) => {
    if (e?.target?.closest('[data-select-cell]')) return;
    if (inlineEdit?.id === contact._id) return;
    // Strip any leading apostrophe from phone (Excel/Apollo CSV artifact)
    const cleaned = contact.phone
      ? { ...contact, phone: contact.phone.replace(/^'+/, '') }
      : contact;
    setSelectedContact(cleaned);
    setEditData(cleaned);
    setShowContactDetail(true);
    setEditMode(false);
    setConfirmDelete(false);
    loadActivities(contact._id);
    loadFiles(contact._id);
  };

  // Open a contact straight from a link (e.g. /contacts?open=<id> from the Twine)
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get('open');
    if (!openId || !contacts.length) return;
    const c = contacts.find((x) => x._id === openId);
    if (c) { handleRowClick(c); window.history.replaceState({}, '', location.pathname); }
  }, [contacts, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // The Twine: suggest what's worth remembering from a logged activity
  const [twineKey, setTwineKey] = useState(0);
  const rememberActivity = async (activity) => {
    try {
      const r = await twineAPI.mend('activity', activity._id);
      const n = r.data.suggested.length;
      toast.success(n ? `${n} suggestion${n > 1 ? 's' : ''} added to the Twine to review` : 'Nothing new to remember');
      twineChanged(); setTwineKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read it'); }
  };

  // Handle stage change
  const handleStageChange = async (picked) => {
    if (!selectedContact) return;
    const newStage = fromStageValue(picked);
    
    const oldStage = selectedContact.stage;
    
    // Optimistic update
    const updatedContact = { ...selectedContact, stage: newStage };
    setSelectedContact(updatedContact);
    setContacts(contacts.map(c => c._id === selectedContact._id ? updatedContact : c));
    
    try {
      await contactsAPI.update(selectedContact._id, { stage: newStage });
      const stageName =
        projectStages.find((s) => s.id === newStage)?.name ||
        STAGES.find((s) => s.id === newStage)?.name ||
        toTitleCase(newStage);
      toast.success(newStage ? `Stage updated to ${stageName}` : 'Taken out of the pipeline');
      loadActivities(selectedContact._id); // Reload to show stage change activity
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Failed to update stage');
      // Revert
      setSelectedContact({ ...selectedContact, stage: oldStage });
      setContacts(contacts.map(c => c._id === selectedContact._id ? { ...selectedContact, stage: oldStage } : c));
    }
  };

  // Add or take the open contact off a project
  const handleProjectToggle = async (projectId, on) => {
    if (!selectedContact) return;
    const before = selectedContact;
    const ids = on ? [...contactProjectIds(before), projectId] : contactProjectIds(before).filter((x) => x !== projectId);
    const optimistic = { ...before, project_ids: ids, project_id: ids[0] || null };
    setSelectedContact(optimistic);
    setContacts((list) => list.map((c) => (c._id === before._id ? optimistic : c)));
    try {
      const r = await contactsAPI.update(before._id, on ? { add_project_ids: [projectId] } : { remove_project_ids: [projectId] });
      setSelectedContact((cur) => (cur?._id === before._id ? { ...cur, ...r.data } : cur));
      setContacts((list) => list.map((c) => (c._id === before._id ? { ...c, ...r.data } : c)));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update projects');
      setSelectedContact(before);
      setContacts((list) => list.map((c) => (c._id === before._id ? before : c)));
    }
  };

  // ── View mode ─────────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState('contacts'); // 'contacts' | 'companies'

  // Group contacts by company for company view
  const groupedByCompany = useMemo(() => {
    const groups = {};
    sortedContacts.forEach(c => {
      const key = c.company || '(No Company)';
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    });
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '(No Company)') return 1;
      if (b === '(No Company)') return -1;
      return a.localeCompare(b);
    });
  }, [sortedContacts]);

  // ── Multi-select & bulk actions ───────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkProjectOpen, setBulkProjectOpen] = useState(false);
  const [bulkProjectId, setBulkProjectId] = useState('');
  const [bulkRepOpen, setBulkRepOpen] = useState(false);
  const [bulkRepEmail, setBulkRepEmail] = useState('');
  const [bulkTypeOpen, setBulkTypeOpen] = useState(false);
  const [bulkTypeValue, setBulkTypeValue] = useState('');
  const [bulkWorking, setBulkWorking] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedContacts.map((c) => c._id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    setBulkWorking(true);
    let deleted = 0;
    for (const id of selectedIds) {
      try { await contactsAPI.delete(id); deleted++; } catch (_) {}
    }
    setContacts((prev) => prev.filter((c) => !selectedIds.has(c._id)));
    clearSelection();
    setBulkDeleteOpen(false);
    setBulkWorking(false);
    toast.success(`Deleted ${deleted} contact${deleted !== 1 ? 's' : ''}`);
  };

  const handleBulkAssignProject = async () => {
    if (!bulkProjectId) return;
    setBulkWorking(true);
    let updated = 0;
    for (const id of selectedIds) {
      try {
        await contactsAPI.update(id, { add_project_ids: [bulkProjectId] });
        updated++;
      } catch (_) {}
    }
    await loadContacts();
    clearSelection();
    setBulkProjectOpen(false);
    setBulkProjectId('');
    setBulkWorking(false);
    toast.success(`Added ${updated} contact${updated !== 1 ? 's' : ''} to ${projects.find((p) => p._id === bulkProjectId)?.name || 'the project'}`);
  };

  const handleBulkAssignRep = async () => {
    if (!bulkRepEmail) return;
    setBulkWorking(true);
    let updated = 0;
    for (const id of selectedIds) {
      try {
        await contactsAPI.update(id, { assigned_to: bulkRepEmail });
        updated++;
      } catch (_) {}
    }
    setContacts(prev => prev.map(c => selectedIds.has(c._id) ? { ...c, assigned_to: bulkRepEmail } : c));
    clearSelection();
    setBulkRepOpen(false);
    setBulkRepEmail('');
    setBulkWorking(false);
    toast.success(`Assigned ${updated} contact${updated !== 1 ? 's' : ''} to rep`);
  };

  const handleBulkAssignType = async () => {
    if (!bulkTypeValue) return;
    setBulkWorking(true);
    let updated = 0;
    for (const id of selectedIds) {
      try {
        await contactsAPI.update(id, { contact_type: bulkTypeValue });
        updated++;
      } catch (_) {}
    }
    setContacts(prev => prev.map(c => selectedIds.has(c._id) ? { ...c, contact_type: bulkTypeValue } : c));
    clearSelection();
    setBulkTypeOpen(false);
    setBulkTypeValue('');
    setBulkWorking(false);
    toast.success(`Updated type for ${updated} contact${updated !== 1 ? 's' : ''}`);
  };

  // ── Inline cell editing ───────────────────────────────────────────────────
  // inlineEdit = { id: contactId, field: fieldName, value: tempValue } | null
  const [inlineEdit, setInlineEdit] = useState(null);

  const startInline = (contact, field) => {
    setInlineEdit({ id: contact._id, field, value: contact[field] ?? '' });
  };

  const commitInline = async () => {
    if (!inlineEdit) return;
    const { id, field, value } = inlineEdit;
    const original = contacts.find((c) => c._id === id);
    if (!original || original[field] === value) {
      setInlineEdit(null);
      return;
    }
    // Optimistic update
    setContacts((prev) => prev.map((c) => c._id === id ? { ...c, [field]: value } : c));
    setInlineEdit(null);
    try {
      await contactsAPI.update(id, { [field]: value });
    } catch (_) {
      // Revert
      setContacts((prev) => prev.map((c) => c._id === id ? { ...c, [field]: original[field] } : c));
      toast.error('Failed to save change');
    }
  };

  const cancelInline = () => setInlineEdit(null);

  // Handle delete contact
  const [confirmDelete, setConfirmDelete] = useState(false);
  const handleDeleteContact = async () => {
    try {
      await contactsAPI.delete(selectedContact._id);
      setContacts(contacts.filter(c => c._id !== selectedContact._id));
      setShowContactDetail(false);
      setConfirmDelete(false);
      toast.success('Contact deleted');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      await contactsAPI.update(selectedContact._id, editData);
      setSelectedContact(editData);
      setContacts(contacts.map(c => c._id === selectedContact._id ? editData : c));
      setEditMode(false);
      toast.success('Contact updated successfully');
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    }
  };

  // Handle log activity
  const handleLogActivity = async (type) => {
    if (!activityFormData.subject && !activityFormData.body) {
      toast.error('Please enter activity details');
      return;
    }
    
    try {
      const activityData = {
        contact_id: selectedContact._id,
        type: type,
        subject: activityFormData.subject || `${type} activity`,
        body: activityFormData.body,
      };
      
      await activitiesAPI.create(activityData);
      toast.success('Activity logged');
      setActivityFormData({ subject: '', body: '' });
      setShowActivityForm(null);
      loadActivities(selectedContact._id);
    } catch (error) {
      console.error('Error logging activity:', error);
      toast.error('Failed to log activity');
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingFile(true);
      await filesAPI.upload(selectedContact._id, file);
      toast.success('File uploaded successfully');
      loadFiles(selectedContact._id);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file. SharePoint may not be configured.');
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle file delete
  const handleFileDelete = async (fileId) => {
    try {
      await filesAPI.delete(selectedContact._id, fileId);
      toast.success('File deleted');
      loadFiles(selectedContact._id);
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  // Handle create contact
  const handleCreateContact = async (data) => {
    try {
      const isCompany = data.kind === 'company';
      await contactsAPI.create({
        ...data,
        kind: data.kind || 'person',
        // A company listing is filed under the company's name.
        full_name: isCompany ? data.company.trim() : data.full_name.trim(),
        stage: fromStageValue(data.stage ?? 'new_lead'),
        project_ids: newContactProjectIds,
      });
      toast.success('Contact created successfully');
      setShowNewContact(false);
      reset(NEW_CONTACT_DEFAULTS);
      loadContacts();
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Failed to create contact');
    }
  };

  // CSV Import Functions

  /**
   * RFC 4180-compliant CSV parser.
   * Handles quoted fields with embedded commas, newlines, and escaped quotes ("").
   */
  const parseCSV = (text) => {
    const records = [];
    let currentRecord = [];
    let currentField = '';
    let inQuotes = false;
    let i = 0;
    // Normalize CRLF → LF
    const csv = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    while (i < csv.length) {
      const ch = csv[i];
      if (inQuotes) {
        if (ch === '"') {
          if (csv[i + 1] === '"') {
            currentField += '"'; // escaped quote
            i += 2;
          } else {
            inQuotes = false;
            i++;
          }
        } else {
          currentField += ch;
          i++;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
          i++;
        } else if (ch === ',') {
          currentRecord.push(currentField.trim());
          currentField = '';
          i++;
        } else if (ch === '\n') {
          currentRecord.push(currentField.trim());
          if (currentRecord.some(f => f !== '')) records.push(currentRecord);
          currentRecord = [];
          currentField = '';
          i++;
        } else {
          currentField += ch;
          i++;
        }
      }
    }
    // Flush last field / record
    currentRecord.push(currentField.trim());
    if (currentRecord.some(f => f !== '')) records.push(currentRecord);

    if (records.length < 1) return [];
    const headers = records[0].map(h => h.toLowerCase().replace(/\s+/g, '_'));
    return records.slice(1).map(values =>
      Object.fromEntries(headers.map((h, idx) => [h, values[idx] || '']))
    );
  };

  /**
   * Normalise a parsed CSV row to our internal contact shape.
   * Handles both our own template columns AND Apollo's native export columns.
   *
   * Apollo export headers (after lowercasing):
   *   first_name, last_name, title, company_name, company_name_for_emails,
   *   email, work_direct_phone, home_phone, mobile_phone, corporate_phone,
   *   other_phone, account_owner, industry, city, state, country, apollo_account_id
   */
  const normalizeRow = (row) => {
    // Phone: prefer work direct, then corporate, then mobile, then home, then other
    const rawPhone = (
      row.work_direct_phone ||
      row.corporate_phone   ||
      row.mobile_phone      ||
      row.home_phone        ||
      row.other_phone       ||
      row.phone             || ''
    );
    // Apollo prefixes phone numbers with a leading apostrophe in CSV to prevent
    // Excel from treating them as formulas — strip it.
    const phone = rawPhone.replace(/^'+/, '').trim();

    // Build notes from Apollo enrichment metadata
    const notesParts = [];
    if (row.industry) notesParts.push(`Industry: ${row.industry}`);
    const location = [row.city, row.state, row.country].filter(Boolean).join(', ');
    if (location) notesParts.push(`Location: ${location}`);
    if (row.notes) notesParts.push(row.notes);

    const fullName =
      row.full_name ||
      [row.first_name, row.last_name].filter(Boolean).join(' ').trim();

    const company =
      row.company ||
      row.company_name ||
      row.company_name_for_emails || '';

    const position = row.position || row.title || '';

    const apolloId = row.apollo_id || row.apollo_account_id || '';

    // Generic external-system reference for the COMPANY (e.g. a provider
    // network sync) — lets re-imports update the same Account instead of
    // creating duplicates. Some exports (e.g. a refreshed Medworks pull) use
    // a composite id like "p:<provider>:l:<location>:c:<contact>" to encode
    // provider/location/contact in one string — pull the provider segment
    // back out so rows for the same provider still group together even when
    // their location/contact suffix differs. Prefer a flat medworks_provider_id
    // column when present since it's unambiguous.
    const composite = /^p:([^:]+)(?::l:([^:]+))?(?::c:([^:]+))?$/.exec((row.external_id || '').trim());
    const externalId = row.medworks_provider_id || (composite ? composite[1] : (row.external_id || ''));
    const locationId = (composite && composite[2]) || row.location_id || '';

    return {
      full_name:    fullName,
      first_name:   row.first_name || '',
      last_name:    row.last_name  || '',
      email:        row.email      || '',
      phone,
      company,
      company_website: row.company_website || row.website || '',
      position,
      contact_type: row.contact_type || 'other',
      source:       row.source ? normalizeSource(row.source) : (apolloId ? 'apollo' : 'manual'),
      linkedin_url: row.linkedin_url || row.linkedin || '',
      street:       row.street || '',
      city:         row.city || '',
      state:        row.state || '',
      postal_code:  row.postal_code || '',
      country:      row.country || '',
      notes:        notesParts.join('\n'),
      project:      row.project || '',
      apollo_id:    apolloId,
      external_id:  externalId,
      location_id:  locationId,
      location_name: row.location_name || '',
    };
  };

  // "General", blank, etc. mean the row has no actual named person — it's
  // just the company's general contact info. Used to route phone/email onto
  // the Account (main_email/main_phone) instead of creating a fake Contact.
  const isGenericName = (name) => {
    const n = (name || '').trim().toLowerCase();
    return n === '' || n === 'general';
  };

  // Account.main_email is stored as-is (lenient — real-world data is messy,
  // e.g. "a@x.com; b@y.com" or a leading "; "). Contact.email is validated
  // server-side, so pull out just the first well-formed address for it.
  const firstValidEmail = (value) => {
    const match = (value || '').split(/[;,\s]+/).find(part => /^\S+@\S+\.\S+$/.test(part));
    return match || '';
  };

  const handleCSVFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const parsed = parseCSV(text).map(normalizeRow);
        setCSVFile(file);
        setCSVPreview(parsed.slice(0, 5)); // Preview first 5 rows
      } catch (error) {
        console.error('Error parsing CSV:', error);
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    // Include both full_name and split first/last options + project column
    const headers = ['full_name', 'first_name', 'last_name', 'email', 'phone', 'company', 'company_website', 'position', 'contact_type', 'source', 'linkedin_url', 'street', 'city', 'state', 'postal_code', 'country', 'notes', 'project', 'external_id', 'location_id', 'location_name'];
    const examples = [
      ['Jane Smith', 'Jane', 'Smith', 'jane@acme.com', '416-555-1234', 'Acme Corp', 'https://acme.com', 'VP Sales', 'lead', 'referral', 'https://linkedin.com/in/janesmith', '123 Main St', 'Toronto', 'ON', 'M5V 2T6', 'Canada', 'Met at conference', projects[0]?.name || '', '', '', ''],
      // Company with no named contact yet — leave name/email blank, use phone/email
      // as the company's general contact info. Import creates the Company only.
      ['', '', '', 'info@othercorp.com', '416-555-9999', 'Other Corp', 'https://othercorp.com', '', 'lead', 'referral', '', '', '', '', '', '', 'General inbox — no named contact yet', projects[0]?.name || '', '', '', ''],
      // Multi-branch example: same company + same external_id, but different
      // location_id/location_name per row — these import as ONE parent
      // company with a branch Account per location, each with its own
      // address/contact info instead of one flat company.
      ['', '', '', 'downtown@chaincorp.com', '416-555-1111', 'Chain Corp', '', '', 'lead', 'referral', '', '10 King St', 'Toronto', 'ON', 'M5H 1A1', 'Canada', 'Downtown branch', projects[0]?.name || '', 'CHAIN-001', 'loc-downtown', 'Downtown'],
      ['', '', '', 'uptown@chaincorp.com', '416-555-2222', 'Chain Corp', '', '', 'lead', 'referral', '', '500 Yonge St', 'Toronto', 'ON', 'M4Y 1X1', 'Canada', 'Uptown branch', projects[0]?.name || '', 'CHAIN-001', 'loc-uptown', 'Uptown'],
    ];
    const csv = headers.join(',') + '\n' + examples.map(e => e.join(',')).join('\n') + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCSVImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    // Build a case-insensitive project name → id lookup
    const projectByName = {};
    (projects || []).forEach(p => {
      projectByName[p.name.toLowerCase().trim()] = p._id;
    });

    // A hung request (bad row, flaky connection) shouldn't block a large
    // sequential import forever with no feedback — cap each network call and
    // treat a timeout as a per-row error so the loop keeps moving.
    const withTimeout = (promise, ms = 20000) =>
      Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), ms)),
      ]);
    const IMPORT_PROGRESS_TOAST_ID = 'csv-import-progress';

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        setImporting(true);
        const text = e.target.result;
        const parsed = parseCSV(text).map(normalizeRow);

        // Project propagation: find the first non-empty project name in the file
        // and use it as the default for rows that don't specify one.
        const fileDefaultProject = parsed.find(r => r.project)?.project || '';
        const fileDefaultProjectId = fileDefaultProject
          ? projectByName[fileDefaultProject.toLowerCase().trim()]
          : undefined;

        // If the dialog has a manually-chosen fallback project, use it when
        // neither the row nor the file specifies a project.
        const dialogFallbackId = csvFallbackProjectId || undefined;

        let skipped = 0;
        let errors = 0;
        let companiesCreated = 0;
        let companiesUpdated = 0;
        let companiesUnchanged = 0;
        let contactsImported = 0;
        let contactsSkipped = 0;

        // ── Group rows by company, then by location within that company ────
        // Rows with no named person (blank or "General") represent that
        // location's general contact info and get merged onto the Account
        // (main_email/main_phone) rather than becoming a fake Contact.
        // Rows with a real name become a Contact linked to that Account.
        //
        // Company grouping key prefers external_id (survives re-imports/
        // renames), falling back to the company name — this matters because
        // a source system can have two different companies that happen to
        // share a display name (they'd have different external ids).
        //
        // Within a company, rows are further split by location_id (present
        // on exports that track multiple branches per provider, e.g. a
        // composite "p:<provider>:l:<location>:c:<contact>" external_id).
        // If a company only ever has one location, behavior is unchanged —
        // a single flat Account. If it has more than one, we create a parent
        // Account for the company and a branch Account per location.
        const groups = new Map();
        const standaloneContacts = []; // rows with a name but no company at all

        for (const row of parsed) {
          const companyName = (row.company || '').trim();
          const hasNamedContact = row.full_name && !isGenericName(row.full_name);

          if (!companyName) {
            if (hasNamedContact && row.email) {
              standaloneContacts.push(row);
            } else {
              skipped++;
            }
            continue;
          }

          const resolvedProjectId =
            (row.project && projectByName[row.project.toLowerCase().trim()]) ||
            fileDefaultProjectId ||
            dialogFallbackId ||
            projects[0]?._id;

          const key = `${resolvedProjectId}::${(row.external_id || companyName.toLowerCase())}`;
          if (!groups.has(key)) {
            groups.set(key, {
              name: companyName,
              project_id: resolvedProjectId,
              external_id: '',
              notes: '',
              locations: new Map(),
            });
          }
          const group = groups.get(key);
          if (!group.external_id && row.external_id) group.external_id = row.external_id;
          if (!group.notes && row.notes) group.notes = row.notes;

          // Prefer an explicit location_id (e.g. Medworks' composite id) when
          // present. Most CSVs won't have one, though — a plain export listing
          // several branches of the same company usually only differs by
          // address/location_name, so fall back to that combination. Without
          // this fallback, every branch row would collapse into a single
          // '__default__' location (first row wins, the rest silently
          // dropped) any time location_id is blank, which is the common case.
          const locationKey = row.location_id
            || [row.location_name, row.street, row.city].filter(Boolean).join('|').toLowerCase()
            || '__default__';
          if (!group.locations.has(locationKey)) {
            group.locations.set(locationKey, {
              location_id: row.location_id || '',
              name: '',
              website: '',
              main_email: '',
              main_phone: '',
              street: '', city: '', state: '', postal_code: '', country: '',
              notes: '',
              contacts: [],
            });
          }
          const loc = group.locations.get(locationKey);
          if (!loc.name && row.location_name) loc.name = row.location_name;
          if (!loc.website && row.company_website) loc.website = row.company_website;
          if (!loc.notes && row.notes) loc.notes = row.notes;
          ['street', 'city', 'state', 'postal_code', 'country'].forEach(f => {
            if (!loc[f] && row[f]) loc[f] = row[f];
          });

          if (hasNamedContact) {
            loc.contacts.push(row);
          } else {
            // General/blank-name row → location-level contact info
            if (!loc.main_email && row.email) loc.main_email = row.email.replace(/^;\s*/, '').trim();
            if (!loc.main_phone && row.phone) loc.main_phone = row.phone.trim();
          }
        }

        // ── Fetch existing accounts + contacts once per project ─────────────
        // So re-running an import (e.g. a refreshed provider-network export)
        // updates the same company/branch instead of duplicating it, and
        // skips contacts that were already created on a prior run.
        const existingByProject = {};
        const existingContactsByAccount = {}; // account_id -> [contact, ...]
        for (const projectId of new Set([...groups.values()].map(g => g.project_id).filter(Boolean))) {
          try {
            const res = await withTimeout(accountsAPI.list({ project_id: projectId }));
            existingByProject[projectId] = Array.isArray(res.data) ? res.data : [];
          } catch {
            existingByProject[projectId] = [];
          }
          try {
            const cRes = await withTimeout(contactsAPI.list({ project_id: projectId }));
            (Array.isArray(cRes.data) ? cRes.data : []).forEach(c => {
              if (!c.account_id) return;
              (existingContactsByAccount[c.account_id] ||= []).push(c);
            });
          } catch {
            // If this fails we just don't dedupe contacts for this project —
            // import still proceeds, worst case is a few re-created contacts.
          }
        }

        // A named-contact row already exists on this account if there's a
        // contact with the same email (preferred) or, failing that, the same
        // full_name. Prevents every re-import from piling up duplicates and
        // makes re-runs fast (skip instead of create) for already-done rows.
        const contactAlreadyExists = (accountId, row) => {
          const existing = existingContactsByAccount[accountId] || [];
          const email = (row.email || '').trim().toLowerCase();
          if (email) return existing.some(c => (c.email || '').trim().toLowerCase() === email);
          const name = (row.full_name || '').trim().toLowerCase();
          return existing.some(c => (c.full_name || '').trim().toLowerCase() === name);
        };

        // Skip an update call entirely when every field we'd send already
        // matches the existing account — on a re-import of an unchanged
        // file this turns hundreds of already-done companies into no-ops
        // instead of a full round-trip each.
        const payloadMatchesExisting = (payload, existing) => {
          if (!existing) return false;
          return Object.entries(payload).every(([k, v]) => {
            if (v === undefined) return true;
            return (existing[k] ?? '') === v;
          });
        };

        // Create/update the named contacts + (if no named contact) the
        // "Company" lookup contact for one location, linked to accountId.
        // `displayName` is what the synthetic "Company" contact is named —
        // the company name for a flat single-location account (a raw
        // location_name like "Brampton" or "Kitchener" would be a confusing
        // contact name on its own), or the branch name when this location is
        // one of several branches under a parent (where the distinguishing
        // label is the whole point).
        const importLocationContacts = async (loc, accountId, projectId, groupNotes, isNewMainInfo, companyName, displayName) => {
          for (const row of loc.contacts) {
            if (contactAlreadyExists(accountId, row)) {
              contactsSkipped++;
              continue;
            }
            try {
              const contactPayload = {
                full_name:    row.full_name,
                email:        row.email || undefined,
                phone:        row.phone || undefined,
                company:      row.company || undefined,
                position:     row.position || undefined,
                contact_type: row.contact_type || csvFallbackContactType || 'other',
                source:       row.source || 'manual',
                linkedin_url: row.linkedin_url || undefined,
                notes:        row.notes || undefined,
                stage:        'new_lead',
                project_id:   projectId,
                account_id:   accountId,
              };
              if (row.apollo_id) contactPayload.apollo_id = row.apollo_id;
              const created = await withTimeout(contactsAPI.create(contactPayload));
              contactsImported++;
              (existingContactsByAccount[accountId] ||= []).push(created.data || contactPayload);
            } catch (error) {
              console.error('Error importing contact:', error);
              errors++;
            }
          }

          // Always give a company-only location a lookup-able Contact card,
          // even when the source row has no email/phone yet (just a name +
          // address) — that's most of a fresh provider-network import, and
          // the whole point of "kind: company" is to make it findable on the
          // Contacts page while outreach fills in the real contact info later.
          if (loc.contacts.length === 0
              && !contactAlreadyExists(accountId, { full_name: displayName || companyName || '(company)', email: '' })) {
            try {
              const created = await withTimeout(contactsAPI.create({
                full_name:    displayName || companyName || '(company)',
                kind:         'company',
                company:      companyName || undefined,
                email:        firstValidEmail(loc.main_email) || undefined,
                phone:        loc.main_phone || undefined,
                contact_type: csvFallbackContactType || 'other',
                source:       'manual',
                notes:        (loc.notes || groupNotes) || undefined,
                stage:        'new_lead',
                project_id:   projectId,
                account_id:   accountId,
              }));
              contactsImported++;
              (existingContactsByAccount[accountId] ||= []).push(created.data);
            } catch (error) {
              console.error('Error importing company contact:', error);
              errors++;
            }
          }
        };

        const totalGroups = groups.size;
        let processedGroups = 0;
        toast.loading(`Importing… 0 / ${totalGroups} companies`, { id: IMPORT_PROGRESS_TOAST_ID });

        for (const group of groups.values()) {
          processedGroups++;
          if (processedGroups % 10 === 0 || processedGroups === totalGroups) {
            toast.loading(`Importing… ${processedGroups} / ${totalGroups} companies`, { id: IMPORT_PROGRESS_TOAST_ID });
          }
          const locationEntries = [...group.locations.values()];
          const totalContactRows = locationEntries.reduce((s, l) => s + l.contacts.length, 0);
          if (!group.project_id) { skipped += 1 + totalContactRows; continue; }

          const existingAccounts = existingByProject[group.project_id] || [];
          const findExisting = (extId, name, parentId) => existingAccounts.find(a =>
            (extId && a.external_id === extId) ||
            (!extId && (a.name || '').trim().toLowerCase() === name.toLowerCase() && (a.parent_account_id || '') === (parentId || ''))
          );

          if (locationEntries.length <= 1) {
            // ── Single location — same flat Account as before ──────────────
            const loc = locationEntries[0] || {
              name: '', website: '', main_email: '', main_phone: '',
              street: '', city: '', state: '', postal_code: '', country: '',
              notes: '', contacts: [],
            };
            const existing = findExisting(group.external_id, group.name, '');
            const isNewMainInfo = !existing || (!existing.main_email && !existing.main_phone);

            const payload = {
              name: group.name,
              project_id: group.project_id,
              website: loc.website || undefined,
              external_id: group.external_id || undefined,
              main_email: loc.main_email || undefined,
              main_phone: loc.main_phone || undefined,
              street: loc.street || undefined,
              city: loc.city || undefined,
              state: loc.state || undefined,
              postal_code: loc.postal_code || undefined,
              country: loc.country || undefined,
              notes: (loc.notes || group.notes) || undefined,
            };

            let accountId;
            try {
              if (existing) {
                accountId = existing.id || existing._id;
                if (payloadMatchesExisting(payload, existing)) {
                  companiesUnchanged++;
                } else {
                  await withTimeout(accountsAPI.update(accountId, payload));
                  companiesUpdated++;
                }
              } else {
                const created = await withTimeout(accountsAPI.create(payload));
                accountId = created.data.id || created.data._id;
                companiesCreated++;
              }
            } catch (error) {
              console.error('Error importing company:', error);
              errors += 1 + loc.contacts.length;
              continue;
            }

            await importLocationContacts(loc, accountId, group.project_id, group.notes, isNewMainInfo, group.name, group.name);
          } else {
            // ── Multiple locations — parent company + a branch per location ─
            const existingParent = findExisting(group.external_id, group.name, '');
            const parentPayload = {
              name: group.name,
              project_id: group.project_id,
              external_id: group.external_id || undefined,
              notes: group.notes || undefined,
            };

            let parentId;
            try {
              if (existingParent) {
                parentId = existingParent.id || existingParent._id;
                if (payloadMatchesExisting(parentPayload, existingParent)) {
                  companiesUnchanged++;
                } else {
                  await withTimeout(accountsAPI.update(parentId, parentPayload));
                  companiesUpdated++;
                }
              } else {
                const created = await withTimeout(accountsAPI.create(parentPayload));
                parentId = created.data.id || created.data._id;
                companiesCreated++;
              }
            } catch (error) {
              console.error('Error importing company:', error);
              errors += 1 + totalContactRows;
              continue;
            }

            for (const loc of locationEntries) {
              const branchExternalId = (group.external_id && loc.location_id) ? `${group.external_id}::${loc.location_id}` : '';
              const branchName = loc.name || [loc.city, loc.state].filter(Boolean).join(', ') || `${group.name} location`;
              const existingBranch = findExisting(branchExternalId, branchName, parentId);
              const isNewMainInfo = !existingBranch || (!existingBranch.main_email && !existingBranch.main_phone);

              const branchPayload = {
                name: branchName,
                project_id: group.project_id,
                parent_account_id: parentId,
                website: loc.website || undefined,
                external_id: branchExternalId || undefined,
                main_email: loc.main_email || undefined,
                main_phone: loc.main_phone || undefined,
                street: loc.street || undefined,
                city: loc.city || undefined,
                state: loc.state || undefined,
                postal_code: loc.postal_code || undefined,
                country: loc.country || undefined,
                notes: loc.notes || undefined,
              };

              let branchId;
              try {
                if (existingBranch) {
                  branchId = existingBranch.id || existingBranch._id;
                  if (payloadMatchesExisting(branchPayload, existingBranch)) {
                    companiesUnchanged++;
                  } else {
                    await withTimeout(accountsAPI.update(branchId, branchPayload));
                    companiesUpdated++;
                  }
                } else {
                  const created = await withTimeout(accountsAPI.create(branchPayload));
                  branchId = created.data.id || created.data._id;
                  companiesCreated++;
                }
              } catch (error) {
                console.error('Error importing branch:', error);
                errors += 1 + loc.contacts.length;
                continue;
              }

              await importLocationContacts(loc, branchId, group.project_id, group.notes, isNewMainInfo, group.name, branchName);
            }
          }
        }

        // ── Standalone contacts (named person, no company) ─────────────────
        for (const row of standaloneContacts) {
          const resolvedProjectId =
            (row.project && projectByName[row.project.toLowerCase().trim()]) ||
            fileDefaultProjectId ||
            dialogFallbackId ||
            projects[0]?._id;
          try {
            const payload = {
              full_name:    row.full_name,
              email:        row.email,
              phone:        row.phone || '',
              position:     row.position || '',
              contact_type: row.contact_type || csvFallbackContactType || 'other',
              source:       row.source || 'manual',
              linkedin_url: row.linkedin_url || '',
              notes:        row.notes || '',
              stage:        'new_lead',
              project_id:   resolvedProjectId,
            };
            if (row.apollo_id) payload.apollo_id = row.apollo_id;
            await withTimeout(contactsAPI.create(payload));
            contactsImported++;
          } catch (error) {
            console.error('Error importing contact:', error);
            errors++;
          }
        }

        toast.success(
          `Imported ${companiesCreated} new companies (${companiesUpdated} updated, ${companiesUnchanged} unchanged) and ${contactsImported} contacts` +
          `${contactsSkipped ? ` (${contactsSkipped} already existed)` : ''}. ` +
          `Skipped: ${skipped}${errors ? `, Errors: ${errors}` : ''}`,
          { id: IMPORT_PROGRESS_TOAST_ID }
        );
        setShowCSVImport(false);
        setCSVFile(null);
        setCSVPreview([]);
        loadContacts();
      } catch (error) {
        console.error('Error importing CSV:', error);
        toast.error('Failed to import contacts', { id: IMPORT_PROGRESS_TOAST_ID });
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(csvFile);
  };

  // Sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getStageColor = (stageId) => {
    return (
      projectStages.find((s) => s.id === stageId)?.color ||
      STAGES.find((s) => s.id === stageId)?.color ||
      'bg-gray-100 text-gray-600'
    );
  };

  const getStageName = (stageId) => {
    return (
      projectStages.find((s) => s.id === stageId)?.name ||
      STAGES.find((s) => s.id === stageId)?.name ||
      (stageId ? toTitleCase(stageId) : '-')
    );
  };

  const getTypeColor = (typeId) => colorFor(typeId);

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const ActivityIcon = ({ type }) => {
    const activityType = ACTIVITY_TYPES.find(t => t.id === type);
    const Icon = activityType?.icon || FileText;
    return <Icon className="w-4 h-4" />;
  };

  const activeFilterCount = [
    selectedProjectIds.length > 0 ? 'projects' : undefined,
    filters.stage, filters.contact_type, filters.assigned_to, filters.source, filters.kind,
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setFilters({ stage: undefined, contact_type: undefined, assigned_to: undefined, source: undefined, kind: undefined });
    setSelectedProjectIds([]);
  };

  return (
    <Layout title={t('contacts.title')}>
      <div className="space-y-4">
        {/* ── Action row ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
            {t('contacts.title')}
          </h1>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex border border-[#E3DDD1] rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('contacts')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'contacts' ? 'bg-[#A62B1F] text-white' : 'text-[#5C6670] hover:bg-[#FBF8F2]'}`}
              >
                <LayoutList className="w-3.5 h-3.5" />By Contact
              </button>
              <button
                onClick={() => setViewMode('companies')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'companies' ? 'bg-[#A62B1F] text-white' : 'text-[#5C6670] hover:bg-[#FBF8F2]'}`}
              >
                <Building2 className="w-3.5 h-3.5" />By Company
              </button>
            </div>
            <Button
              variant="outline"
              onClick={handleDedup}
              disabled={deduping}
              title="Remove contacts with duplicate email addresses"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deduping ? 'Removing…' : 'Remove Duplicates'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCSVImport(true)}
              data-testid="contacts-import-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('contacts.import_csv')}
            </Button>
            <Button
              style={{ backgroundColor: '#A62B1F', fontFamily: 'Manrope' }}
              onClick={() => setShowNewContact(true)}
              data-testid="contacts-add-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('contacts.add_contact')}
            </Button>
          </div>
        </div>

        {/* ── Filter bar ──────────────────────────────────────────────── */}
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center gap-2">
              {/* Search */}
              <div className="relative flex-1 min-w-[160px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6670]" />
                <Input
                  placeholder={t('contacts.search')}
                  className="pl-8 h-9 text-sm"
                  style={{ fontFamily: 'Manrope' }}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  data-testid="contacts-search-input"
                />
              </div>

              {/* Project — multi-select checkbox popover */}
              {projects.length > 1 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 gap-1.5 text-sm font-normal min-w-[130px] justify-between">
                      <span className="truncate">
                        {selectedProjectIds.length === 0
                          ? t('contacts.all_projects')
                          : selectedProjectIds.length === 1
                            ? (projects.find(p => p._id === selectedProjectIds[0])?.name || 'Project')
                            : `${selectedProjectIds.length} projects`}
                      </span>
                      <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-52 p-2" align="start">
                    <div className="space-y-1">
                      <button
                        onClick={() => setSelectedProjectIds([])}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-[#FBF8F2] ${selectedProjectIds.length === 0 ? 'font-semibold text-[#A62B1F]' : 'text-[#5C6670]'}`}
                      >
                        All projects
                      </button>
                      <div className="h-px bg-[#E3DDD1] my-1" />
                      {projects.map(p => {
                        const checked = selectedProjectIds.includes(p._id);
                        return (
                          <label key={p._id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#FBF8F2] cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              className="h-3.5 w-3.5 rounded border-[#D3CCBE] accent-[#A62B1F] cursor-pointer"
                              onChange={() => {
                                setSelectedProjectIds(prev =>
                                  checked ? prev.filter(id => id !== p._id) : [...prev, p._id]
                                );
                              }}
                            />
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#9AA3AB' }} />
                            <span className="text-sm text-[#1E2A33] truncate">{p.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {/* Stage */}
              <Select
                value={filters.stage || 'all'}
                onValueChange={(v) => setFilters(f => ({ ...f, stage: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-9 w-[130px] text-sm">
                  <SelectValue placeholder={t('contacts.all_stages')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('contacts.all_stages')}</SelectItem>
                  {projectStages.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Type */}
              <Select
                value={filters.contact_type || 'all'}
                onValueChange={(v) => setFilters(f => ({ ...f, contact_type: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-9 w-[130px] text-sm">
                  <SelectValue placeholder={t('contacts.all_types')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('contacts.all_types')}</SelectItem>
                  {CONTACT_TYPES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Assigned To */}
              {teamMembers.length > 0 && (
                <Select
                  value={filters.assigned_to || 'all'}
                  onValueChange={(v) => setFilters(f => ({ ...f, assigned_to: v === 'all' ? undefined : v }))}
                >
                  <SelectTrigger className="h-9 w-[130px] text-sm">
                    <SelectValue placeholder={t('contacts.all_reps')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('contacts.all_reps')}</SelectItem>
                    {teamMembers.filter(m => m.is_active).map(m => (
                      <SelectItem key={m.email} value={m.email}>
                        {m.full_name || m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Source */}
              <Select
                value={filters.source || 'all'}
                onValueChange={(v) => setFilters(f => ({ ...f, source: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-9 w-[120px] text-sm">
                  <SelectValue placeholder={t('contacts.all_sources')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('contacts.all_sources')}</SelectItem>
                  {SOURCES.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Type: Person vs Company */}
              <Select
                value={filters.kind || 'all'}
                onValueChange={(v) => setFilters(f => ({ ...f, kind: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-9 w-[130px] text-sm">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {KINDS.map(k => (
                    <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Clear filters */}
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 text-xs text-[#5C6670] hover:text-[#1E2A33]"
                  onClick={clearAllFilters}
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  {t('contacts.clear_filters')} ({activeFilterCount})
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contact Count + Bulk Action Bar */}
        <div className="flex items-center justify-between min-h-[36px]">
          <p className="text-sm text-[#5C6670]" style={{ fontFamily: 'Manrope' }}>
            {t('contacts.showing')} <span className="font-semibold text-[#1E2A33]">{sortedContacts.length}</span> {t('contacts.contacts')}
          </p>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 bg-[#FBF8F2] border border-[#E3DDD1] rounded-lg px-3 py-1.5 shadow-sm">
              <CheckSquare2 className="w-4 h-4 text-[#A62B1F]" />
              <span className="text-sm font-medium text-[#1E2A33]">
                {selectedIds.size} {t('contacts.selected')}
              </span>
              <div className="w-px h-4 bg-[#E3DDD1]" />
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                onClick={() => { setBulkProjectId(projects[0]?._id || ''); setBulkProjectOpen(true); }}
              >
                <FolderInput className="w-3.5 h-3.5" />
                {t('contacts.assign_project')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                onClick={() => { setBulkRepEmail(''); setBulkRepOpen(true); }}
              >
                <UserCheck className="w-3.5 h-3.5" />
                {t('contacts.assign_rep')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                onClick={() => { setBulkTypeValue(''); setBulkTypeOpen(true); }}
              >
                <Tag className="w-3.5 h-3.5" />
                Assign Type
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setBulkDeleteOpen(true)}
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t('common.delete')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-[#5C6670]"
                onClick={clearSelection}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>

        {/* ── By Company view ─────────────────────────────────────────────── */}
        {viewMode === 'companies' && (
          <div className="space-y-3">
            {groupedByCompany.length === 0 ? (
              <p className="text-sm text-[#5C6670] text-center py-10">{t('contacts.no_results')}</p>
            ) : groupedByCompany.map(([company, companyContacts]) => (
              <Card key={company}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4 text-[#A62B1F]" />
                    <span className="font-semibold text-[#1E2A33] text-sm">{company}</span>
                    <Badge variant="outline" className="text-xs ml-1">{companyContacts.length}</Badge>
                  </div>
                  <div className="space-y-1">
                    {companyContacts.map(c => (
                      <div
                        key={c._id}
                        onClick={() => { setSelectedContact(c); setShowContactDetail(true); }}
                        className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-[#F8EFED] cursor-pointer transition-colors group"
                      >
                        <Avatar className="h-7 w-7 flex-shrink-0">
                          <AvatarFallback className="text-xs bg-[#F7ECEA] text-[#A62B1F]">
                            {getInitials(c.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1E2A33] truncate">{c.full_name}</p>
                          {c.position && <p className="text-xs text-[#5C6670] truncate">{c.position}</p>}
                        </div>
                        {c.email && <p className="text-xs text-[#5C6670] truncate max-w-[160px] hidden sm:block">{c.email}</p>}
                        {c.contact_type && (
                          <Badge variant="outline" className={`text-xs flex-shrink-0 ${colorFor(c.contact_type)}`}>
                            {labelFor(c.contact_type)}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ── By Contact (table) view ──────────────────────────────────────── */}
        {viewMode === 'contacts' && <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* Select-all checkbox */}
                  <TableHead className="w-10" data-select-cell>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-[#D3CCBE] cursor-pointer"
                      checked={sortedContacts.length > 0 && selectedIds.size === sortedContacts.length}
                      ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < sortedContacts.length; }}
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('full_name')}
                    data-testid="sort-name"
                  >
                    <div className="flex items-center gap-2">
                      {t('contacts.col_name')}
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('company')}
                    data-testid="sort-company"
                  >
                    <div className="flex items-center gap-2">
                      {t('contacts.col_company')}
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </TableHead>
                  <TableHead>{t('contacts.col_contact_info')}</TableHead>
                  <TableHead>{t('contacts.col_type')}</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('stage')}
                    data-testid="sort-stage"
                  >
                    <div className="flex items-center gap-2">
                      {t('contacts.col_stage')}
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </TableHead>
                  <TableHead>{t('contacts.col_source')}</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('created_at')}
                    data-testid="sort-created"
                  >
                    <div className="flex items-center gap-2">
                      {t('contacts.col_created')}
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-[#5C6670]">
                      {t('contacts.loading')}
                    </TableCell>
                  </TableRow>
                ) : sortedContacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-[#5C6670]">
                      {t('contacts.none_found')}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedContacts.map((contact) => {
                    const isSelected = selectedIds.has(contact._id);
                    const isEditingRow = inlineEdit?.id === contact._id;

                    const InlineText = ({ field, className = '' }) => {
                      const editing = isEditingRow && inlineEdit.field === field;
                      if (editing) {
                        return (
                          <Input
                            autoFocus
                            value={inlineEdit.value}
                            className={`h-7 text-sm px-1.5 py-0 ${className}`}
                            onChange={(e) => setInlineEdit((s) => ({ ...s, value: e.target.value }))}
                            onBlur={commitInline}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitInline();
                              if (e.key === 'Escape') cancelInline();
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        );
                      }
                      return (
                        <span
                          className={`cursor-text group/inline relative ${className}`}
                          onDoubleClick={(e) => { e.stopPropagation(); startInline(contact, field); }}
                          title="Double-click to edit"
                        >
                          {contact[field] || <span className="text-[#9AA3AB] italic text-xs">—</span>}
                          <span className="absolute right-0 top-0 hidden group-hover/inline:inline-block text-[#9AA3AB] pl-1">
                            <Edit2 className="w-3 h-3 inline" />
                          </span>
                        </span>
                      );
                    };

                    const InlineSelect = ({ field, options, getLabel, getColor }) => {
                      const editing = isEditingRow && inlineEdit.field === field;
                      if (editing) {
                        return (
                          <Select
                            value={inlineEdit.value || (field === 'stage' ? NO_STAGE : inlineEdit.value)}
                            onValueChange={(picked) => {
                              const v = field === 'stage' ? fromStageValue(picked) : picked;
                              // Immediately save — no need to wait for blur
                              const original = contacts.find((c) => c._id === contact._id);
                              setContacts((prev) => prev.map((c) => c._id === contact._id ? { ...c, [field]: v } : c));
                              setInlineEdit(null);
                              contactsAPI.update(contact._id, { [field]: v }).catch(() => {
                                setContacts((prev) => prev.map((c) => c._id === contact._id ? { ...c, [field]: original?.[field] } : c));
                                toast.error('Failed to save change');
                              });
                            }}
                          >
                            <SelectTrigger
                              className="h-7 text-xs w-auto min-w-[110px]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent onClick={(e) => e.stopPropagation()}>
                              {options.map((o) => (
                                <SelectItem key={o.id} value={o.id}>{o.name || o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      }
                      const val = contact[field];
                      return (
                        <Badge
                          variant="outline"
                          className={`${getColor(val)} cursor-pointer`}
                          onClick={(e) => { e.stopPropagation(); startInline(contact, field); }}
                          title="Click to change"
                        >
                          {getLabel(val)}
                        </Badge>
                      );
                    };

                    return (
                      <TableRow
                        key={contact._id}
                        className={`cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-[#F3F0FF]' : ''}`}
                        onClick={(e) => handleRowClick(contact, e)}
                        data-testid={`contact-row-${contact._id}`}
                      >
                        {/* Checkbox */}
                        <TableCell className="w-10" data-select-cell onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-[#D3CCBE] cursor-pointer"
                            checked={isSelected}
                            onChange={() => toggleSelect(contact._id)}
                            aria-label={`Select ${contact.full_name}`}
                          />
                        </TableCell>

                        {/* Name — double-click to edit */}
                        <TableCell className="font-semibold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                          <div className="flex items-center gap-1.5">
                            <InlineText field="full_name" />
                            {contact.kind === 'company' && (
                              <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal text-[#A62B1F] border-[#D99A90]">Company</Badge>
                            )}
                            {contact.email_opt_out && (
                              <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal text-amber-700 border-amber-300 bg-amber-50" title="This contact unsubscribed — campaign emails are blocked">Unsubscribed</Badge>
                            )}
                          </div>
                          {contact.position && (
                            <div className="text-xs text-[#5C6670] font-normal mt-0.5">
                              <InlineText field="position" className="font-normal text-xs text-[#5C6670]" />
                            </div>
                          )}
                        </TableCell>

                        {/* Company */}
                        <TableCell className="text-[#5C6670]">
                          <InlineText field="company" />
                        </TableCell>

                        {/* Contact Info — email double-click to edit */}
                        <TableCell>
                          <div className="flex flex-col gap-1 text-sm">
                            <div className="flex items-center gap-1 text-[#5C6670]">
                              <Mail className="w-3 h-3 flex-shrink-0" />
                              <InlineText field="email" className="truncate max-w-[180px]" />
                            </div>
                            {contact.phone && (
                              <div className="flex items-center gap-1 text-[#5C6670]">
                                <Phone className="w-3 h-3" />
                                <span>{contact.phone}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>

                        {/* Type — double-click to edit */}
                        <TableCell>
                          <InlineSelect
                            field="contact_type"
                            options={CONTACT_TYPES}
                            getLabel={(v) => labelFor(v) || v}
                            getColor={getTypeColor}
                          />
                        </TableCell>

                        {/* Stage — double-click to edit */}
                        <TableCell>
                          <InlineSelect
                            field="stage"
                            options={[...stagesFor(projects, contactProjectIds(contact)), NO_STAGE_OPTION]}
                            getLabel={(v) => (v ? getStageName(v) : 'No stage')}
                            getColor={getStageColor}
                          />
                        </TableCell>

                        <TableCell>
                          <Badge variant="outline" className={`font-medium whitespace-nowrap ${SOURCE_STYLES[contact.source] || 'bg-[#F3EFE7] text-[#5C6670] border-[#E3DDD1]'}`}>
                            {SOURCES.find((s) => s.id === contact.source)?.label || contact.source || 'Unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-[#5C6670]">
                          {safeFormat(contact.created_at, 'MMM dd, yyyy', { fallback: '-' })}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>}
      </div>

      {/* Contact Detail Sheet */}
      <Sheet open={showContactDetail} onOpenChange={setShowContactDetail}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedContact && (
          <DetailErrorBoundary onReset={() => setShowContactDetail(false)}>
            <>
              <SheetHeader>
                <div className="flex items-start gap-4">
                  <Avatar className="h-16 w-16" style={{ backgroundColor: '#A62B1F' }}>
                    <AvatarFallback className="text-white text-xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                      {getInitials(selectedContact.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    {editMode ? (
                      <div className="space-y-1">
                        <Input
                          value={editData.full_name || ''}
                          onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                          className="text-lg font-semibold"
                          placeholder="Full name"
                        />
                        <Input
                          value={editData.company || ''}
                          onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                          className="text-sm"
                          placeholder="Company"
                        />
                      </div>
                    ) : (
                      <>
                        <SheetTitle className="text-2xl text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                          {selectedContact.full_name}
                        </SheetTitle>
                        <SheetDescription className="text-[#5C6670]">
                          {[selectedContact.position, selectedContact.company].filter(Boolean).join(' · ') || 'No company'}
                        </SheetDescription>
                        {selectedContact.email_opt_out && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className="text-xs font-normal text-amber-700 border-amber-300 bg-amber-50">
                              Unsubscribed from email{selectedContact.email_opt_out_at ? ` · ${new Date(selectedContact.email_opt_out_at).toLocaleDateString()}` : ''}
                            </Badge>
                            {canResubscribe && (
                              <button
                                type="button"
                                onClick={() => setResubOpen(true)}
                                className="text-xs text-[#5C6670] underline hover:text-[#1E2A33]"
                              >
                                Re-subscribe…
                              </button>
                            )}
                          </div>
                        )}
                        <Dialog open={resubOpen} onOpenChange={(o) => { setResubOpen(o); if (!o) setResubNote(''); }}>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle>Re-subscribe {selectedContact.full_name}?</DialogTitle>
                              <DialogDescription>
                                Only do this if they have explicitly asked to receive your emails again. Re-adding someone
                                who unsubscribed without their consent breaks CAN-SPAM and CASL. Your note is kept on their timeline.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-2">
                              <Label htmlFor="resub-note" className="text-sm">How did they opt back in?</Label>
                              <Textarea
                                id="resub-note"
                                value={resubNote}
                                onChange={(e) => setResubNote(e.target.value)}
                                placeholder="e.g. Replied on 19 Oct asking to be added back to the list"
                                rows={3}
                              />
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                              <Button variant="outline" onClick={() => setResubOpen(false)}>Cancel</Button>
                              <Button
                                onClick={handleResubscribe}
                                disabled={resubSaving || resubNote.trim().length < 5}
                                style={{ backgroundColor: '#A62B1F', color: 'white' }}
                              >
                                {resubSaving ? 'Saving…' : 'Re-subscribe'}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                  </div>
                  <Button
                    variant={editMode ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      if (editMode) {
                        handleSaveEdit();
                      } else {
                        setEditMode(true);
                      }
                    }}
                    style={editMode ? { backgroundColor: '#A62B1F' } : {}}
                    data-testid="contact-edit-button"
                  >
                    {editMode ? (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {t('common.save')}
                      </>
                    ) : (
                      <>
                        <Edit2 className="w-4 h-4 mr-2" />
                        {t('common.edit')}
                      </>
                    )}
                  </Button>
                  {editMode && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditMode(false);
                        setEditData(selectedContact);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Stage Selector */}
                <div>
                  <Label className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>{t('contacts.col_stage')}</Label>
                  {(() => {
                    const opts = stagesFor(projects, contactProjectIds(selectedContact));
                    if (selectedContact.stage && !opts.some((s) => s.id === selectedContact.stage)) {
                      opts.push({ id: selectedContact.stage, name: getStageName(selectedContact.stage) });
                    }
                    return (
                      <Select value={selectedContact.stage || NO_STAGE} onValueChange={handleStageChange}>
                        <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {opts.map((stage) => <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>)}
                          <SelectItem value={NO_STAGE}>{NO_STAGE_OPTION.name}</SelectItem>
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>

                {projects.length > 0 && (
                  <div>
                    <Label className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>Projects</Label>
                    <ProjectChips projects={projects} value={contactProjectIds(selectedContact)} onToggle={handleProjectToggle} />
                  </div>
                )}

                <Separator />

                {/* Contact Fields */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>{t('contacts.contact_info')}</h3>
                  
                  <div>
                    <Label className="text-[#5C6670]">{t('common.email')}</Label>
                    {editMode ? (
                      <Input
                        value={editData.email || ''}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <p className="text-[#1E2A33] mt-1">{selectedContact.email || '-'}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">{t('common.phone')}</Label>
                    {editMode ? (
                      <Input
                        value={editData.phone || ''}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <p className="text-[#1E2A33] mt-1">{selectedContact.phone ? selectedContact.phone.replace(/^'+/, '') : '-'}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">{t('contacts.position_title')}</Label>
                    {editMode ? (
                      <Input
                        value={editData.position || ''}
                        onChange={(e) => setEditData({ ...editData, position: e.target.value })}
                        placeholder="e.g. Sales Manager"
                        className="mt-1"
                      />
                    ) : (
                      <p className="text-[#1E2A33] mt-1">{selectedContact.position || '-'}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">{t('contacts.linkedin_url')}</Label>
                    {editMode ? (
                      <Input
                        value={editData.linkedin_url || ''}
                        onChange={(e) => setEditData({ ...editData, linkedin_url: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <p className="text-[#1E2A33] mt-1">
                        {selectedContact.linkedin_url ? (
                          <a href={selectedContact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {selectedContact.linkedin_url}
                          </a>
                        ) : '-'}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">Address</Label>
                    {editMode ? (
                      <div className="space-y-2 mt-1">
                        <Input
                          value={editData.street || ''}
                          onChange={(e) => setEditData({ ...editData, street: e.target.value })}
                          placeholder="Street Address"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={editData.city || ''}
                            onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                            placeholder="City"
                          />
                          <Input
                            value={editData.state || ''}
                            onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                            placeholder="State / Province"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={editData.postal_code || ''}
                            onChange={(e) => setEditData({ ...editData, postal_code: e.target.value })}
                            placeholder="Postal Code"
                          />
                          <Input
                            value={editData.country || ''}
                            onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                            placeholder="Country"
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-[#1E2A33] mt-1">
                        {[selectedContact.street, selectedContact.city, selectedContact.state, selectedContact.postal_code, selectedContact.country].filter(Boolean).join(', ') || '-'}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">Language</Label>
                    {editMode ? (
                      <Select
                        value={editData.language || 'en'}
                        onValueChange={(value) => setEditData({ ...editData, language: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="es">Spanish</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[#1E2A33] mt-1">
                        {selectedContact.language === 'es' ? 'Spanish' : 'English'}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">Type</Label>
                    {editMode ? (
                      <Select
                        value={editData.kind || 'person'}
                        onValueChange={(value) => setEditData({ ...editData, kind: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {KINDS.map(k => (
                            <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[#1E2A33] mt-1">
                        {KINDS.find(k => k.id === selectedContact.kind)?.label || 'Person'}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">{t('contacts.contact_type')}</Label>
                    {editMode ? (
                      <Select
                        value={editData.contact_type}
                        onValueChange={(value) => setEditData({ ...editData, contact_type: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTACT_TYPES.map(type => (
                            <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[#1E2A33] mt-1">
                        {labelFor(selectedContact.contact_type) || selectedContact.contact_type}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">{t('common.source')}</Label>
                    {editMode ? (
                      <Select
                        value={editData.source} 
                        onValueChange={(value) => setEditData({ ...editData, source: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SOURCES.map(source => (
                            <SelectItem key={source.id} value={source.id}>{source.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[#1E2A33] mt-1">
                        {SOURCES.find(s => s.id === selectedContact.source)?.label || selectedContact.source || '-'}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">{t('common.assigned_to')}</Label>
                    <p className="text-[#1E2A33] mt-1">
                      {selectedContact.assigned_to === currentUser?.id
                        ? (currentUser?.full_name || currentUser?.email || 'You')
                        : selectedContact.assigned_to
                          ? (currentUser?.full_name || currentUser?.email || selectedContact.assigned_to)
                          : '-'}
                    </p>
                  </div>

                  <div>
                    <Label className="text-[#5C6670]">{t('common.notes')}</Label>
                    {editMode ? (
                      <Textarea
                        value={editData.notes || ''}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        className="mt-1"
                        rows={3}
                      />
                    ) : (
                      <p className="text-[#1E2A33] mt-1 whitespace-pre-wrap">{selectedContact.notes || '-'}</p>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Notes about this person or company (shared with the team unless made private) */}
                <NotesPanel key={`notes-${selectedContact._id}`} contactId={selectedContact._id}
                  emptyText="Call notes, what they care about, follow-ups. Lines starting with [ ] can become tasks." />

                <Separator />

                {/* The Twine */}
                <TwinePanel key={`${selectedContact._id}-${twineKey}`}
                  subject={{ type: 'contact', id: selectedContact._id, label: selectedContact.full_name }} />

                <Separator />

                {/* Activity Feed */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>{t('contacts.activity_feed')}</h3>
                  </div>

                  {/* Quick Log Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {ACTIVITY_TYPES.slice(0, 4).map(type => (
                      <Button
                        key={type.id}
                        variant="outline"
                        size="sm"
                        onClick={() => setShowActivityForm(type.id)}
                        data-testid={`log-${type.id}-button`}
                      >
                        <type.icon className="w-4 h-4 mr-2" />
                        {t(`contacts.log_${type.id}`)}
                      </Button>
                    ))}
                  </div>

                  {/* Activity Form */}
                  {showActivityForm && (
                    <Card className="bg-muted/30">
                      <CardContent className="pt-4 space-y-3">
                        <Input
                          placeholder={t('contacts.subject')}
                          value={activityFormData.subject}
                          onChange={(e) => setActivityFormData({ ...activityFormData, subject: e.target.value })}
                          data-testid="activity-subject-input"
                        />
                        <Textarea
                          placeholder={t('contacts.details')}
                          value={activityFormData.body}
                          onChange={(e) => setActivityFormData({ ...activityFormData, body: e.target.value })}
                          rows={3}
                          data-testid="activity-body-input"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            style={{ backgroundColor: '#A62B1F' }}
                            onClick={() => handleLogActivity(showActivityForm)}
                            data-testid="activity-submit-button"
                          >
                            {t('contacts.save_activity')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setShowActivityForm(null);
                              setActivityFormData({ subject: '', body: '' });
                            }}
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Activities List */}
                  <div className="space-y-3">
                    {loadingActivities ? (
                      <p className="text-sm text-[#5C6670]">{t('contacts.loading_activities')}</p>
                    ) : activities.length === 0 ? (
                      <p className="text-sm text-[#5C6670]">{t('contacts.no_activities')}</p>
                    ) : (
                      activities.map((activity) => (
                        <div 
                          key={activity._id} 
                          className="flex gap-3 p-3 border border-[#E3DDD1] rounded-lg"
                          data-testid={`activity-${activity._id}`}
                        >
                          <div className="mt-1">
                            <ActivityIcon type={activity.type} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#1E2A33] text-sm">{activity.subject}</p>
                            {activity.body && (
                              /<[a-z][\s\S]*>/i.test(activity.body) ? (
                                <div
                                  className="text-sm text-[#5C6670] mt-1 prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline"
                                  dangerouslySetInnerHTML={{ __html: activity.body }}
                                />
                              ) : (
                                <p className="text-sm text-[#5C6670] mt-1 whitespace-pre-wrap">{activity.body}</p>
                              )
                            )}
                            <div className="flex items-center gap-2 mt-2 text-xs text-[#5C6670]">
                              <span>{activity.performed_by}</span>
                              <span>•</span>
                              <span>
                                {safeFormat(activity.occurred_at, 'MMM dd, yyyy h:mm a', { fallback: 'Unknown date' })}
                              </span>
                              {activity.type !== 'stage_change' && activity.body && (
                                <button type="button" className="ml-auto inline-flex items-center gap-1 text-[#A62B1F] hover:underline"
                                  title="Suggest what's worth remembering from this" onClick={() => rememberActivity(activity)}>
                                  <Sparkles className="w-3 h-3" />Remember
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <Separator />

                {/* Danger zone */}
                <div className="space-y-2">
                  {!confirmDelete ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 border-red-200 hover:bg-red-50 hover:border-red-400"
                      onClick={() => setConfirmDelete(true)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('contacts.delete_contact')}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700 flex-1">{t('contacts.confirm_delete')}</p>
                      <Button
                        size="sm"
                        className="bg-red-500 hover:bg-red-600 text-white"
                        onClick={handleDeleteContact}
                      >
                        {t('common.delete')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDelete(false)}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  )}
                </div>

                <Separator />

                {/* SharePoint Files */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>{t('contacts.files')}</h3>
                    <div>
                      <input
                        id="contact-file-upload"
                        type="file"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploadingFile}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={uploadingFile}
                        onClick={(e) => {
                          e.preventDefault();
                          const input = document.getElementById('contact-file-upload');
                          if (input) input.click();
                        }}
                        data-testid="upload-file-button"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingFile ? t('contacts.uploading_file') : t('contacts.upload_file')}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {loadingFiles ? (
                      <p className="text-sm text-[#5C6670]">{t('contacts.loading_files')}</p>
                    ) : files.length === 0 ? (
                      <p className="text-sm text-[#5C6670]">{t('contacts.no_files')}</p>
                    ) : (
                      files.map((file) => (
                        <div 
                          key={file.id} 
                          className="flex items-center justify-between p-3 border border-[#E3DDD1] rounded-lg"
                          data-testid={`file-${file.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-[#5C6670]" />
                            <div>
                              <p className="text-sm font-medium text-[#1E2A33]">{file.name}</p>
                              <p className="text-xs text-[#5C6670]">
                                {safeFormat(file.uploaded_at, 'MMM dd, yyyy', { fallback: 'Unknown date' })}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {file.web_url ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(file.web_url, '_blank')}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Download"
                                onClick={() => filesAPI.download(selectedContact._id, file.id, file.name).catch(() => toast.error('Download failed'))}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleFileDelete(file.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </>
          </DetailErrorBoundary>
          )}
        </SheetContent>
      </Sheet>

      {/* New Contact Sheet */}
      <Sheet open={showNewContact} onOpenChange={setShowNewContact}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>{t('contacts.new_contact_title')}</SheetTitle>
            <SheetDescription className="text-[#5C6670]">
              {t('contacts.new_contact_desc')}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit(handleCreateContact)} className="mt-6 space-y-4">
            <div>
              <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-[#F3EFE7]">
                {KINDS.map((k) => (
                  <button key={k.id} type="button" data-testid={`new-contact-kind-${k.id}`}
                    onClick={() => setValue('kind', k.id, { shouldValidate: false })}
                    className={`py-1.5 rounded-md text-sm font-medium transition-colors ${newKind === k.id ? 'bg-white text-[#1E2A33] shadow-sm' : 'text-[#5C6670]'}`}>
                    {k.id === 'company' ? 'Company (no named person)' : 'Person'}
                  </button>
                ))}
              </div>
            </div>

            {newKind === 'company' ? (
              <div key="company-name">
                <Label className="text-[#1E2A33]">Company name *</Label>
                <Input {...register('company')} className="mt-1" data-testid="new-contact-company" />
                {errors.company && <p className="text-sm text-red-500 mt-1">{errors.company.message}</p>}
                <p className="text-xs text-[#9AA3AB] mt-1">For a general listing: main line, info@ address. Add named people later.</p>
              </div>
            ) : (
              <div key="person-name" className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[#1E2A33]">{t('contacts.full_name')} *</Label>
                  <Input {...register('full_name')} className="mt-1" data-testid="new-contact-name" />
                  {errors.full_name && <p className="text-sm text-red-500 mt-1">{errors.full_name.message}</p>}
                </div>
                <div>
                  <Label className="text-[#1E2A33]">{t('common.company')}</Label>
                  <Input {...register('company')} className="mt-1" data-testid="new-contact-company" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-[#1E2A33]">{newKind === 'company' ? 'General email' : t('common.email')}</Label>
              <Input
                {...register('email')}
                type="email"
                className="mt-1"
                data-testid="new-contact-email"
              />
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label className="text-[#1E2A33]">{t('common.phone')}</Label>
              <Input
                {...register('phone')}
                className="mt-1"
                data-testid="new-contact-phone"
              />
            </div>

            {newKind !== 'company' && <div>
              <Label className="text-[#1E2A33]">{t('contacts.position_title')}</Label>
              <Input
                {...register('position')}
                placeholder={t('contacts.eg_sales_manager')}
                className="mt-1"
              />
            </div>}

            {/* Pipeline stage: stages come from the chosen project's pipeline */}
            <div>
              <Label className="text-[#1E2A33]">Pipeline stage</Label>
              <Select value={newStage} onValueChange={(v) => setValue('stage', v)}>
                <SelectTrigger className="mt-1" data-testid="new-contact-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    const opts = stagesFor(projects, newContactProjectIds);
                    return [...opts, ...(opts.some((x) => x.id === newStage) || newStage === NO_STAGE ? [] : [{ id: newStage, name: getStageName(newStage) }])];
                  })().map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
                  <SelectItem value={NO_STAGE}>{NO_STAGE_OPTION.name} (supplier, partner, on file)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <details className="rounded-lg border border-[#E3DDD1] px-3 py-2">
              <summary className="text-sm font-medium text-[#1E2A33] cursor-pointer select-none">More details <span className="text-[#9AA3AB] font-normal">(address, language)</span></summary>
              <div className="space-y-4 pt-3">
            <div>
              <Label className="text-[#1E2A33]">Street Address</Label>
              <Input {...register('street')} className="mt-1" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[#1E2A33]">City</Label>
                <Input {...register('city')} className="mt-1" />
              </div>
              <div>
                <Label className="text-[#1E2A33]">State / Province</Label>
                <Input {...register('state')} className="mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[#1E2A33]">Postal Code</Label>
                <Input {...register('postal_code')} className="mt-1" />
              </div>
              <div>
                <Label className="text-[#1E2A33]">Country</Label>
                <Input {...register('country')} className="mt-1" />
              </div>
            </div>

            <div>
              <Label className="text-[#1E2A33]">Language</Label>
              <Select onValueChange={(value) => setValue('language', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="English (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#9AA3AB] mt-1">Which language version of campaign emails this contact receives.</p>
            </div>
              </div>
            </details>

            {projects.length > 0 && (
              <div>
                <Label className="text-[#1E2A33]">Projects</Label>
                <ProjectChips projects={projects} value={newContactProjectIds}
                  onToggle={(id, on) => setNewContactProjectIds(on ? [...newContactProjectIds, id] : newContactProjectIds.filter((x) => x !== id))} />
                <p className="text-xs text-[#9AA3AB] mt-1">Pick every project this person is part of, or none for now.</p>
              </div>
            )}

            <div>
              <Label className="text-[#1E2A33]">{t('contacts.contact_type')} *</Label>
              <Select onValueChange={(value) => setValue('contact_type', value, { shouldValidate: true })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('contacts.select_type')} />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.contact_type && (
                <p className="text-sm text-red-500 mt-1">{errors.contact_type.message}</p>
              )}
            </div>

            <div>
              <Label className="text-[#1E2A33]">{t('common.source')}</Label>
              <Select onValueChange={(value) => setValue('source', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('contacts.select_source')} />
                </SelectTrigger>
                <SelectContent>
                  {SOURCES.map(source => (
                    <SelectItem key={source.id} value={source.id}>{source.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#1E2A33]">{t('common.assigned_to')}</Label>
              <Input
                {...register('assigned_to')}
                placeholder={t('contacts.assigned_email')}
                className="mt-1"
                data-testid="new-contact-assigned"
              />
              {errors.assigned_to && (
                <p className="text-sm text-red-500 mt-1">{errors.assigned_to.message}</p>
              )}
            </div>

            <div>
              <Label className="text-[#1E2A33]">{t('contacts.linkedin_url')}</Label>
              <Input
                {...register('linkedin_url')}
                placeholder="https://linkedin.com/in/..."
                className="mt-1"
                data-testid="new-contact-linkedin"
              />
              {errors.linkedin_url && (
                <p className="text-sm text-red-500 mt-1">{errors.linkedin_url.message}</p>
              )}
            </div>

            <div>
              <Label className="text-[#1E2A33]">{t('common.notes')}</Label>
              <Textarea
                {...register('notes')}
                className="mt-1"
                rows={3}
                data-testid="new-contact-notes"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="submit"
                style={{ backgroundColor: '#A62B1F', fontFamily: 'Manrope' }}
                data-testid="new-contact-submit"
              >
                {t('contacts.create_contact')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowNewContact(false);
                  reset();
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* CSV Import Dialog */}
      <Dialog open={showCSVImport} onOpenChange={setShowCSVImport}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
              {t('contacts.csv_title')}
            </DialogTitle>
            <DialogDescription className="text-[#5C6670]">
              {t('contacts.csv_desc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-[#F3EFE7] p-4 rounded-lg">
              <p className="text-sm text-[#5C6670]">
                <strong>Instructions:</strong> Download a CSV template or upload a CSV exported directly from Apollo, a provider network, or any CRM.
                <br />
                <strong>Required:</strong> company (or a full_name if there's no company)
                <br />
                <strong>Rows with a name</strong> (e.g. "Jane Smith") become a Contact linked to that company. <strong>Rows with no name — or "General"</strong> — are treated as the company's general info: their email/phone are saved as the company's Main Email / Main Phone instead of a fake contact.
                <br />
                <strong>Re-importing</strong> the same file (or a refreshed export) updates existing companies instead of duplicating them — matched by external_id if present, otherwise by company name.
                <br />
                <strong>Multiple branches per company:</strong> if rows for the same company (same external_id) have different location_id values, they're imported as a parent company with a branch Account per location (using location_name as the branch name) instead of one flat company. A composite external_id like "p:&lt;provider&gt;:l:&lt;location&gt;:c:&lt;contact&gt;" is parsed automatically.
                <br />
                <strong>Apollo exports</strong> are supported natively — Title, Company Name, phone columns, and Apollo Account Id are mapped automatically.
                <br />
                <strong>Optional:</strong> phone, company_website, position, contact_type, source, linkedin_url, notes, project, external_id, location_id, location_name, street, city, state, postal_code, country
              </p>
            </div>

            {/* Template Download Button */}
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              data-testid="download-template-button"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('contacts.download_template')}
            </Button>

            {/* File Input */}
            <div>
              <Label className="text-[#1E2A33]">{t('contacts.upload_csv')}</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={handleCSVFileSelect}
                className="mt-1"
                data-testid="csv-file-input"
              />
            </div>

            {/* Fallback selectors row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1E2A33]">{t('contacts.assign_project_default')} <span className="text-[#5C6670] font-normal">({t('common.default')})</span></Label>
                <Select
                  value={csvFallbackProjectId || (projects[0]?._id ?? '')}
                  onValueChange={setCsvFallbackProjectId}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a project…" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#1E2A33]">{t('contacts.contact_type_default')} <span className="text-[#5C6670] font-normal">({t('common.default')})</span></Label>
                <Select
                  value={csvFallbackContactType || 'other'}
                  onValueChange={setCsvFallbackContactType}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select type…" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_TYPES.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview Table */}
            {csvPreview.length > 0 && (
              <div>
                <Label className="text-[#1E2A33] mb-2">{t('contacts.preview_rows')}</Label>
                <div className="border border-[#E3DDD1] rounded-lg overflow-auto max-h-64">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead>Project</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvPreview.map((row, index) => {
                        const hasName = row.full_name && !isGenericName(row.full_name);
                        const willSkip = !row.company && !hasName;
                        return (
                          <TableRow key={index}>
                            <TableCell>{row.company || (willSkip ? <span className="text-red-400 text-xs">missing — will skip</span> : '-')}</TableCell>
                            <TableCell>
                              {hasName
                                ? row.full_name
                                : <span className="text-[#5C6670] text-xs italic">general info{row.company ? ' (no contact)' : ''}</span>}
                            </TableCell>
                            <TableCell>{row.email || '-'}</TableCell>
                            <TableCell>{row.phone || '-'}</TableCell>
                            <TableCell>{row.position || '-'}</TableCell>
                            <TableCell>{row.project || <span className="text-[#5C6670] text-xs italic">default</span>}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Import Button */}
            <div className="flex gap-2">
              <Button
                style={{ backgroundColor: '#A62B1F', fontFamily: 'Manrope' }}
                onClick={handleCSVImport}
                disabled={!csvFile || importing}
                data-testid="import-contacts-button"
              >
                {importing ? t('common.importing') : `${t('contacts.import_contacts')} ${csvPreview.length > 0 ? csvPreview.length + '+' : ''}`}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCSVImport(false);
                  setCSVFile(null);
                  setCSVPreview([]);
                  setCsvFallbackProjectId('');
                  setCsvFallbackContactType('');
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirm */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contacts.bulk_delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contacts.bulk_delete_desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkWorking}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkWorking}
              className="bg-red-600 hover:bg-red-700"
            >
              {bulkWorking ? t('common.deleting') : `${t('common.delete')} ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Assign Project */}
      <AlertDialog open={bulkProjectOpen} onOpenChange={setBulkProjectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contacts.assign_to_project')}</AlertDialogTitle>
            <AlertDialogDescription>
              Adds the selected contacts to this project. They stay in any projects they're already part of.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Select value={bulkProjectId} onValueChange={setBulkProjectId}>
              <SelectTrigger>
                <SelectValue placeholder={t('contacts.choose_project')} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p._id} value={p._id}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: p.color || '#A62B1F' }} />
                      {p.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkWorking}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkAssignProject}
              disabled={bulkWorking || !bulkProjectId}
              style={{ backgroundColor: '#A62B1F' }}
            >
              {bulkWorking ? t('common.assigning') : t('common.assign')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Assign Rep */}
      <AlertDialog open={bulkRepOpen} onOpenChange={setBulkRepOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contacts.assign_to_rep')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contacts.assign_to_rep')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Select value={bulkRepEmail} onValueChange={setBulkRepEmail}>
              <SelectTrigger>
                <SelectValue placeholder={t('contacts.choose_rep')} />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map((m) => (
                  <SelectItem key={m.email} value={m.email}>
                    {m.full_name || m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkWorking}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkAssignRep}
              disabled={bulkWorking || !bulkRepEmail}
              style={{ backgroundColor: '#A62B1F' }}
            >
              {bulkWorking ? t('common.assigning') : t('common.assign')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Assign Type */}
      <AlertDialog open={bulkTypeOpen} onOpenChange={setBulkTypeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign Contact Type</AlertDialogTitle>
            <AlertDialogDescription>
              Set the type for {selectedIds.size} selected contact{selectedIds.size !== 1 ? 's' : ''}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Select value={bulkTypeValue} onValueChange={setBulkTypeValue}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a type…" />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_TYPES.map((ct) => (
                  <SelectItem key={ct.id} value={ct.id}>{ct.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkWorking}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkAssignType}
              disabled={bulkWorking || !bulkTypeValue}
              style={{ backgroundColor: '#A62B1F' }}
            >
              {bulkWorking ? 'Assigning…' : 'Assign'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
